import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, FileText, Users, DollarSign, Package,
  Unlock, Lock, Loader, CheckCircle, AlertCircle, X,
  AlertTriangle, ArrowRight, Clock, TrendingUp
} from 'lucide-react';
import { getCaisseActive, ouvrirCaisse, fermerCaisse, getAlertesStock } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useBoutique } from '../context/BoutiqueContext';
import './DashboardCaissier.css';

const ACTIONS = [
  { label: 'Nouvelle vente',  sub: 'Enregistrer une vente',     path: '/ventes/nouvelle', icon: ShoppingCart, color: '#6366f1', grad: 'linear-gradient(135deg,#6366f1,#8b5cf6)' },
  { label: 'Factures',        sub: 'Consulter les factures',     path: '/factures',        icon: FileText,     color: '#0ea5e9', grad: 'linear-gradient(135deg,#0ea5e9,#38bdf8)' },
  { label: 'Clients',         sub: 'Gérer les clients',          path: '/clients',         icon: Users,        color: '#10b981', grad: 'linear-gradient(135deg,#10b981,#34d399)' },
  { label: 'Dettes',          sub: 'Encaisser les acomptes',     path: '/dettes',          icon: DollarSign,   color: '#f59e0b', grad: 'linear-gradient(135deg,#f59e0b,#fbbf24)' },
];

export default function DashboardCaissier() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedBoutiqueId } = useBoutique();

  const [caisse,        setCaisse]        = useState(undefined);
  const [alertes,       setAlertes]       = useState([]);
  const [caisseModal,   setCaisseModal]   = useState(null);
  const [fondCaisse,    setFondCaisse]    = useState('');
  const [montantCompte, setMontantCompte] = useState('');
  const [note,          setNote]          = useState('');
  const [submitting,    setSubmitting]    = useState(false);
  const [toast,         setToast]         = useState(null);
  const [clock,         setClock]         = useState(new Date());

  const bid = (selectedBoutiqueId && selectedBoutiqueId !== 'all') ? selectedBoutiqueId : undefined;

  const showToast = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 3500); };

  useEffect(() => { loadCaisse(); loadAlertes(); }, [selectedBoutiqueId]); // eslint-disable-line
  useEffect(() => { const t = setInterval(() => setClock(new Date()), 30000); return () => clearInterval(t); }, []);

  const loadCaisse = async () => {
    try { const r = await getCaisseActive(bid); setCaisse(r.data.data); }
    catch { setCaisse(null); }
  };
  const loadAlertes = async () => {
    try { const r = await getAlertesStock(); setAlertes(r.data.data || []); }
    catch { setAlertes([]); }
  };

  const handleOuvrir = async () => {
    setSubmitting(true);
    try {
      const p = { fondCaisse: parseFloat(fondCaisse) || 0, noteOuverture: note };
      if (bid) p.boutiqueId = bid;
      await ouvrirCaisse(p);
      showToast('success', 'Caisse ouverte avec succès');
      setCaisseModal(null); setFondCaisse(''); setNote('');
      loadCaisse();
    } catch (e) { showToast('error', e.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleFermer = async () => {
    if (montantCompte === '') { showToast('error', 'Saisissez le montant compté'); return; }
    setSubmitting(true);
    try {
      const p = { montantCompteCaisse: parseFloat(montantCompte) || 0, noteFermeture: note };
      if (bid) p.boutiqueId = bid;
      await fermerCaisse(p);
      showToast('success', 'Caisse fermée avec succès');
      setCaisseModal(null); setMontantCompte(''); setNote('');
      loadCaisse();
    } catch (e) { showToast('error', e.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const heure = clock.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const date  = clock.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const prenom = user?.prenom || user?.nom || 'Caissier';

  return (
    <div className="cai-page">

      {/* ── Toast ── */}
      {toast && (
        <div className={`cai-toast cai-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={14}/> : <AlertCircle size={14}/>}
          {toast.text}
        </div>
      )}

      {/* ══════════════════════════════════════
          HERO HEADER
         ══════════════════════════════════════ */}
      <div className="cai-hero">
        <div className="cai-hero-bg"/>
        <div className="cai-hero-inner">

          {/* Colonne gauche : greeting */}
          <div className="cai-hero-left">
            <p className="cai-hero-time"><Clock size={13}/> {heure}</p>
            <h1 className="cai-hero-title">Bonjour, {prenom}</h1>
            <p className="cai-hero-date">{date}</p>
          </div>

          {/* Colonne droite : statut caisse */}
          <div className="cai-hero-right">
            {caisse === undefined ? (
              <div className="cai-hero-caisse cai-hero-caisse--loading">
                <Loader size={18} className="cai-spin"/>
                <span>Chargement…</span>
              </div>
            ) : caisse ? (
              <div className="cai-hero-caisse cai-hero-caisse--open">
                <div className="cai-hero-caisse-top">
                  <div className="cai-pulse-dot"/>
                  <span className="cai-hero-caisse-status">Caisse ouverte</span>
                </div>
                <p className="cai-hero-caisse-since">
                  Depuis {new Date(caisse.dateOuverture).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
                <button className="cai-hero-caisse-btn cai-hero-caisse-btn--close"
                  onClick={() => { setCaisseModal('fermer'); setNote(''); setMontantCompte(''); }}>
                  <Lock size={13}/> Fermer la caisse
                </button>
              </div>
            ) : (
              <div className="cai-hero-caisse cai-hero-caisse--closed">
                <div className="cai-hero-caisse-top">
                  <div className="cai-closed-dot"/>
                  <span className="cai-hero-caisse-status">Caisse fermée</span>
                </div>
                <p className="cai-hero-caisse-since">Ouvrez la caisse pour commencer</p>
                <button className="cai-hero-caisse-btn cai-hero-caisse-btn--open"
                  onClick={() => { setCaisseModal('ouvrir'); setNote(''); setFondCaisse(''); }}>
                  <Unlock size={13}/> Ouvrir la caisse
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ══════════════════════════════════════
          CONTENU
         ══════════════════════════════════════ */}
      <div className="cai-content">

        {/* ── Section titre raccourcis ── */}
        <div className="cai-section-head">
          <TrendingUp size={15} className="cai-section-ico"/>
          <span>Accès rapide</span>
        </div>

        {/* ── Grille d'actions ── */}
        <div className="cai-grid">
          {ACTIONS.map((a, i) => {
            const Icon = a.icon;
            return (
              <button key={a.path} className="cai-card" onClick={() => navigate(a.path)}
                style={{ '--grad': a.grad, '--clr': a.color, animationDelay: `${i * 60}ms` }}>
                <div className="cai-card-ico-wrap">
                  <div className="cai-card-ico"><Icon size={22}/></div>
                </div>
                <div className="cai-card-body">
                  <span className="cai-card-label">{a.label}</span>
                  <span className="cai-card-sub">{a.sub}</span>
                </div>
                <ArrowRight size={16} className="cai-card-arrow"/>
              </button>
            );
          })}
        </div>

        {/* ── Alertes stock ── */}
        {alertes.length > 0 && (
          <div className="cai-alert-section">
            <div className="cai-section-head cai-section-head--warn">
              <AlertTriangle size={14}/>
              <span>{alertes.length} produit{alertes.length > 1 ? 's' : ''} en stock bas</span>
            </div>
            <div className="cai-alert-grid">
              {alertes.slice(0, 6).map(p => (
                <div key={p._id} className={`cai-alert-pill${p.quantiteStock === 0 ? ' cai-alert-pill--zero' : ''}`}>
                  <Package size={12}/>
                  <span className="cai-alert-pill-name">{p.nom}</span>
                  <span className="cai-alert-pill-qty">
                    {p.quantiteStock === 0 ? 'Rupture' : `${p.quantiteStock} u`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* ══════════════════════════════════════
          MODALS
         ══════════════════════════════════════ */}
      {(caisseModal === 'ouvrir' || caisseModal === 'fermer') && (
        <>
          <div className="cai-overlay" onClick={() => !submitting && setCaisseModal(null)}/>
          <div className="cai-modal">
            <div className="cai-modal-head">
              <div className="cai-modal-head-ico">
                {caisseModal === 'ouvrir' ? <Unlock size={16}/> : <Lock size={16}/>}
              </div>
              <div>
                <h2 className="cai-modal-title">
                  {caisseModal === 'ouvrir' ? 'Ouvrir la caisse' : 'Fermer la caisse'}
                </h2>
                <p className="cai-modal-sub">
                  {clock.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
              <button className="cai-modal-x" onClick={() => setCaisseModal(null)} disabled={submitting}><X size={16}/></button>
            </div>

            <div className="cai-modal-body">
              {caisseModal === 'ouvrir' ? (
                <>
                  <div className="cai-modal-field">
                    <label>Fond de caisse (XOF)</label>
                    <input type="number" min="0" step="500" autoFocus placeholder="0"
                      value={fondCaisse} onChange={e => setFondCaisse(e.target.value)}/>
                    <span className="cai-modal-hint">Montant en espèces présent à l'ouverture</span>
                  </div>
                  <div className="cai-modal-field">
                    <label>Note <span className="cai-opt">(optionnel)</span></label>
                    <input type="text" placeholder="Remarque…" value={note} onChange={e => setNote(e.target.value)}/>
                  </div>
                </>
              ) : (
                <>
                  <div className="cai-modal-field">
                    <label>Montant compté en caisse (XOF) *</label>
                    <input type="number" min="0" step="500" autoFocus required
                      placeholder="Montant physiquement présent"
                      value={montantCompte} onChange={e => setMontantCompte(e.target.value)}/>
                  </div>
                  <div className="cai-modal-field">
                    <label>Note <span className="cai-opt">(optionnel)</span></label>
                    <input type="text" placeholder="Remarque…" value={note} onChange={e => setNote(e.target.value)}/>
                  </div>
                </>
              )}
            </div>

            <div className="cai-modal-footer">
              <button className="cai-btn-cancel" onClick={() => setCaisseModal(null)} disabled={submitting}>Annuler</button>
              {caisseModal === 'ouvrir' ? (
                <button className="cai-btn-action cai-btn-action--open" onClick={handleOuvrir} disabled={submitting}>
                  {submitting ? <><Loader size={13} className="cai-spin"/> Ouverture…</> : <><Unlock size={13}/> Ouvrir</>}
                </button>
              ) : (
                <button className="cai-btn-action cai-btn-action--close" onClick={handleFermer} disabled={submitting}>
                  {submitting ? <><Loader size={13} className="cai-spin"/> Fermeture…</> : <><Lock size={13}/> Fermer</>}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
